import React, { useState, useEffect } from 'react';
import { X, Link, Mail, AlertCircle, Lock, Eye, EyeOff, Clock, CalendarClock, Crown } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SendOptionsModal = ({ 
  files, 
  onClose, 
  onSendLink, 
  onSendEmail, 
  uploading,
  isPro = false,
  userEmail = ''
}) => {
  const [sendMode, setSendMode] = useState('link');
  const [senderEmail, setSenderEmail] = useState(userEmail);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [enablePassword, setEnablePassword] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [planLimits, setPlanLimits] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/plan-limits`).then(res => setPlanLimits(res.data)).catch(() => {});
  }, []);

  const passwordAllowed = isPro || (planLimits ? planLimits.password_protection_free === true : false);
  const scheduledAllowed = isPro || (planLimits ? planLimits.scheduled_send_free === true : false);
  
  const maxFilesFree = planLimits ? planLimits.max_files_free : 5;
  const isOverLimit = !isPro && maxFilesFree > 0 && files.length > maxFilesFree;

  // Build min date (now) and max date (30 days)
  const now = new Date();
  const minDate = now.toISOString().split('T')[0];
  const maxDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  
  const handleSend = () => {
    const passwordToSend = enablePassword && password.length >= 4 ? password : null;
    
    // Build scheduled_at ISO string if scheduling
    let scheduledAt = null;
    if (showScheduler && scheduledDate && scheduledTime && sendMode === 'email') {
      scheduledAt = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
    }
    
    if (sendMode === 'link') {
      onSendLink(passwordToSend);
    } else {
      onSendEmail({ senderEmail, recipientEmail, message, scheduledAt }, passwordToSend);
    }
  };

  const isEmailValid = sendMode === 'email' 
    ? senderEmail.includes('@') && recipientEmail.includes('@')
    : true;
    
  const isPasswordValid = !enablePassword || password.length >= 4;
  const isScheduleValid = !showScheduler || (scheduledDate && scheduledTime);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-blue-950 to-slate-900 rounded-2xl border border-blue-500/30 shadow-2xl max-w-md w-full animate-in zoom-in-95">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-blue-500/20">
          <h2 className="text-xl font-semibold text-white">Envoyer vos fichiers</h2>
          <button onClick={onClose} className="text-blue-300 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Limite dépassée */}
        {isOverLimit && (
          <div className="mx-5 mt-5 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-200 font-medium">Limite dépassée</p>
                <p className="text-amber-200/60 text-sm mt-1">
                  Version gratuite limitée à {maxFilesFree} fichiers. Seuls les {maxFilesFree} premiers seront envoyés.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="p-5 space-y-4">
          {/* Mode tabs */}
          <div className="flex bg-slate-800/50 rounded-xl p-1">
            <button
              onClick={() => setSendMode('link')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                sendMode === 'link' ? 'bg-blue-500/30 text-white' : 'text-blue-300/60 hover:text-blue-300'
              }`}
              data-testid="send-mode-link"
            >
              <Link className="w-4 h-4" /> Obtenir un lien
            </button>
            <button
              onClick={() => setSendMode('email')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                sendMode === 'email' ? 'bg-blue-500/30 text-white' : 'text-blue-300/60 hover:text-blue-300'
              }`}
              data-testid="send-mode-email"
            >
              <Mail className="w-4 h-4" /> Envoyer par email
            </button>
          </div>

          {/* Email fields */}
          {sendMode === 'email' && (
            <div className="space-y-3">
              <input
                type="email"
                placeholder="Votre email"
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                className="w-full bg-slate-800/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400 text-sm"
                data-testid="sender-email-input"
              />
              <input
                type="email"
                placeholder="Email du destinataire"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="w-full bg-slate-800/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400 text-sm"
                data-testid="recipient-email-input"
              />
              <textarea
                placeholder="Message (optionnel)"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
                className="w-full bg-slate-800/50 border border-blue-500/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400 text-sm resize-none"
              />

              {/* Schedule button */}
              {scheduledAllowed ? (
                <>
                  <button
                    onClick={() => setShowScheduler(!showScheduler)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                      showScheduler
                        ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                        : 'text-blue-300/60 hover:text-blue-300 hover:bg-slate-800/50 border border-transparent'
                    }`}
                    data-testid="schedule-toggle-btn"
                  >
                    <Clock className="w-4 h-4" />
                    {showScheduler ? 'Annuler la programmation' : 'Programmer l\'envoi'}
                  </button>

                  {showScheduler && (
                    <div className="bg-slate-800/70 border border-blue-500/20 rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm text-blue-300">
                        <CalendarClock className="w-4 h-4" />
                        <span className="font-medium">Programmer l'envoi</span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-blue-300/50 block mb-1">Date</label>
                          <input
                            type="date"
                            value={scheduledDate}
                            onChange={(e) => setScheduledDate(e.target.value)}
                            min={minDate}
                            max={maxDate}
                            className="w-full bg-slate-900 border border-blue-500/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                            data-testid="schedule-date"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-blue-300/50 block mb-1">Heure</label>
                          <input
                            type="time"
                            value={scheduledTime}
                            onChange={(e) => setScheduledTime(e.target.value)}
                            className="w-full bg-slate-900 border border-blue-500/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-blue-400"
                            data-testid="schedule-time"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-blue-300/40">
                        Maximum 30 jours. Le fichier sera disponible immédiatement, l'email sera envoyé à la date choisie.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 px-3 py-2 text-sm text-blue-300/30">
                  <Clock className="w-4 h-4" />
                  Programmer l'envoi
                  <span className="ml-auto text-xs bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Crown className="w-3 h-3" /> Pro
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Password option */}
          <div className="border-t border-blue-500/10 pt-4">
            {passwordAllowed ? (
              <>
                <button
                  onClick={() => setEnablePassword(!enablePassword)}
                  className={`flex items-center gap-2 text-sm transition-colors ${
                    enablePassword ? 'text-blue-300' : 'text-blue-300/50 hover:text-blue-300'
                  }`}
                  data-testid="password-toggle"
                >
                  <Lock className="w-4 h-4" />
                  Protéger par mot de passe
                </button>
                
                {enablePassword && (
                  <div className="mt-3 relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Mot de passe (min. 4 caractères)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full bg-slate-800/50 border border-blue-500/20 rounded-xl px-4 py-3 pr-10 text-white placeholder-blue-300/40 focus:outline-none focus:border-blue-400 text-sm"
                      data-testid="password-input"
                    />
                    <button
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-300/50 hover:text-blue-300"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    {password.length > 0 && password.length < 4 && (
                      <p className="text-red-400 text-xs mt-1">4 caractères minimum</p>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-blue-300/30">
                <Lock className="w-4 h-4" />
                Protéger par mot de passe
                <span className="ml-auto text-xs bg-yellow-500/15 text-yellow-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Crown className="w-3 h-3" /> Pro
                </span>
              </div>
            )}
          </div>

          {/* File summary */}
          <div className="bg-slate-800/30 rounded-xl p-3">
            <p className="text-sm text-blue-300/70">
              {files.length} fichier{files.length > 1 ? 's' : ''} ({(() => {
                const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
                if (totalBytes < 1024) return `${totalBytes} o`;
                if (totalBytes < 1024 * 1024) return `${(totalBytes / 1024).toFixed(0)} Ko`;
                if (totalBytes < 1024 * 1024 * 1024) return `${(totalBytes / 1024 / 1024).toFixed(1)} Mo`;
                return `${(totalBytes / 1024 / 1024 / 1024).toFixed(2)} Go`;
              })()})
            </p>
          </div>

          {/* Send button */}
          <Button
            onClick={handleSend}
            disabled={uploading || !isEmailValid || !isPasswordValid || (showScheduler && !isScheduleValid)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-base disabled:opacity-50"
            data-testid="send-files-btn"
          >
            {uploading ? (
              'Envoi en cours...'
            ) : showScheduler && scheduledDate && scheduledTime && sendMode === 'email' ? (
              <span className="flex items-center gap-2">
                <CalendarClock className="w-4 h-4" />
                Programmer l'envoi
              </span>
            ) : sendMode === 'link' ? (
              'Obtenir le lien'
            ) : (
              'Envoyer'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendOptionsModal;
